/**
 * The inline markdown grammar the guide pages render, and the one way to remove it.
 *
 * Two consumers, deliberately sharing one implementation:
 *
 * - `RichText` (discoverSections.tsx) turns these markers into `<strong>`, `<em>`, `<code>`
 *   and `<a>` for the DOM.
 * - `faqPageLd` (marketing/seo/jsonLd.ts) strips them, because JSON-LD carries plain strings
 *   and Google and ChatGPT read the structured data rather than the DOM. `**Gimpo**` reached
 *   the FAQPage schema with its asterisks intact on 48 geos.
 *
 * Living in `utils/` rather than in `discoverSections.tsx` so the SEO layer does not have to
 * import a discover component module to get it, and so a second regex never grows next to the
 * first — the renderer and the stripper have to agree on what a marker is, or the schema keeps
 * a marker the page has already removed.
 */

/** Matches one inline marker. Bold is listed first so `**[label](href)**` matches whole. */
export const INLINE_MD =
	/(\*\*(?:(?!\*\*)[\s\S])*?\*?\*\*|__[^_]+__|\[[^\]]+\]\([^)]+\)|`[^`]+`|(?<![*\w])\*[^*\n]+\*(?![*\w])|(?<![_\w])_[^_\n]+_(?![_\w]))/g;

/**
 * The same markers `RichText` understands, removed from a plain string.
 *
 * A link keeps its label and drops its target: the schema wants the sentence a reader sees,
 * and a bare URL in an FAQ answer reads as noise to both a person and a crawler.
 */
export function stripInlineMd(text: string): string {
	return text
		.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
		.replace(/\*\*((?:(?!\*\*)[\s\S])*?\*?)\*\*/g, "$1")
		.replace(/__(.*?)__/g, "$1")
		.replace(/`([^`]*)`/g, "$1")
		.replace(/(?<![*\w])\*([^*\n]+)\*(?![*\w])/g, "$1")
		.replace(/(?<![_\w])_([^_\n]+)_(?![_\w])/g, "$1");
}
